import {
  canonicalizeStudioValue,
} from "../StudioSerializationEngine";

export const PUBLICATION_SCHEMA_VERSION = 1;

export const PUBLICATION_STATUS = Object.freeze({
  PUBLISHED: "published",
  UNPUBLISHED: "unpublished",
});

const REQUIRED_REPOSITORY_METHODS = Object.freeze([
  "publishPublication",
  "loadPublication",
  "loadCurrentPublication",
  "listPublications",
  "unpublishPublication",
  "exists",
]);

export class PublicationError extends Error {
  constructor(
    message,
    {
      code = "PUBLICATION_ERROR",
      operation = null,
      publicationId = null,
      versionId = null,
      documentId = null,
      cause = null,
      details = {},
    } = {}
  ) {
    super(message);
    this.name = "PublicationError";
    this.code = code;
    this.operation = operation;
    this.publicationId = publicationId;
    this.versionId = versionId;
    this.documentId = documentId;
    this.cause = cause;
    this.details =
      details &&
      typeof details === "object" &&
      !Array.isArray(details)
        ? { ...details }
        : {};
  }
}

export function createPublicationEngine({
  repository,
  getVersion,
  createId = createDefaultPublicationId,
  clock = createDefaultTimestamp,
} = {}) {
  validateRepository(repository);
  validateCallback(
    getVersion,
    "getVersion",
    "INVALID_VERSION_RESOLVER"
  );
  validateCallback(
    createId,
    "createId",
    "INVALID_ID_FACTORY"
  );
  validateCallback(
    clock,
    "clock",
    "INVALID_CLOCK"
  );

  async function publishVersion(
    versionId,
    {
      documentId,
      publicationId,
      publishedBy = null,
      metadata = {},
      context,
    } = {}
  ) {
    const operation = "publishVersion";
    const normalizedVersionId =
      normalizeRequiredString(
        versionId,
        "versionId",
        "INVALID_VERSION_ID"
      );

    let normalizedDocumentId = null;
    let normalizedPublicationId = null;

    try {
      const version = await getVersion(
        normalizedVersionId,
        { context }
      );

      validateResolvedVersion(
        version,
        normalizedVersionId
      );

      normalizedDocumentId =
        normalizeRequiredString(
          version.documentId,
          "documentId",
          "INVALID_DOCUMENT_ID"
        );

      if (documentId !== undefined) {
        const expectedDocumentId =
          normalizeRequiredString(
            documentId,
            "documentId",
            "INVALID_DOCUMENT_ID"
          );

        if (
          expectedDocumentId !==
          normalizedDocumentId
        ) {
          throw new PublicationError(
            "Layout version does not belong to the expected Studio document.",
            {
              code: "VERSION_DOCUMENT_MISMATCH",
              operation,
              versionId: normalizedVersionId,
              documentId: expectedDocumentId,
              details: {
                versionDocumentId:
                  normalizedDocumentId,
              },
            }
          );
        }
      }

      normalizedPublicationId =
        normalizeRequiredString(
          publicationId ||
            createId({
              prefix: "publication",
              versionId: normalizedVersionId,
              documentId: normalizedDocumentId,
            }),
          "publicationId",
          "INVALID_PUBLICATION_ID"
        );

      const timestamp =
        normalizeTimestamp(clock());

      const record =
        canonicalizeStudioValue({
          schemaVersion:
            PUBLICATION_SCHEMA_VERSION,
          publicationId:
            normalizedPublicationId,
          documentId:
            normalizedDocumentId,
          versionId:
            normalizedVersionId,
          status:
            PUBLICATION_STATUS.PUBLISHED,
          publishedAt:
            timestamp,
          publishedBy:
            normalizeOptionalString(
              publishedBy,
              "publishedBy"
            ),
          unpublishedAt: null,
          unpublishedBy: null,
          metadata:
            normalizeMetadata(metadata),
        });

      const stored =
        await repository.publishPublication(
          record,
          {
            unpublishedAt: timestamp,
            unpublishedBy:
              normalizeOptionalString(
                publishedBy,
                "publishedBy"
              ),
          },
          context
        );

      return validateAndClonePublication(
        stored ?? record
      );
    } catch (error) {
      throw wrapPublicationError(
        error,
        "Unable to publish Studio layout version.",
        {
          code: "PUBLISH_FAILED",
          operation,
          publicationId:
            normalizedPublicationId,
          versionId:
            normalizedVersionId,
          documentId:
            normalizedDocumentId,
        }
      );
    }
  }

  async function getPublication(
    publicationId,
    { context } = {}
  ) {
    const operation = "getPublication";
    const normalizedPublicationId =
      normalizePublicationId(publicationId);

    try {
      const record =
        await repository.loadPublication(
          normalizedPublicationId,
          context
        );

      if (record === null) {
        throw new PublicationError(
          `Publication not found: ${normalizedPublicationId}.`,
          {
            code: "PUBLICATION_NOT_FOUND",
            operation,
            publicationId:
              normalizedPublicationId,
          }
        );
      }

      return validateAndClonePublication(record);
    } catch (error) {
      throw wrapPublicationError(
        error,
        "Unable to load Studio publication.",
        {
          code: "GET_PUBLICATION_FAILED",
          operation,
          publicationId:
            normalizedPublicationId,
        }
      );
    }
  }

  async function getCurrentPublication(
    documentId,
    { context } = {}
  ) {
    const operation =
      "getCurrentPublication";
    const normalizedDocumentId =
      normalizeDocumentId(documentId);

    try {
      const record =
        await repository.loadCurrentPublication(
          normalizedDocumentId,
          context
        );

      if (record === null) {
        return null;
      }

      const publication =
        validateAndClonePublication(record);

      if (
        publication.documentId !==
        normalizedDocumentId ||
        publication.status !==
          PUBLICATION_STATUS.PUBLISHED
      ) {
        throw new PublicationError(
          "Publication repository returned an invalid current publication.",
          {
            code:
              "INVALID_REPOSITORY_RESULT",
            operation,
            publicationId:
              publication.publicationId,
            documentId:
              normalizedDocumentId,
          }
        );
      }

      return publication;
    } catch (error) {
      throw wrapPublicationError(
        error,
        "Unable to load current Studio publication.",
        {
          code:
            "GET_CURRENT_PUBLICATION_FAILED",
          operation,
          documentId:
            normalizedDocumentId,
        }
      );
    }
  }

  async function listPublications(
    documentId,
    { context } = {}
  ) {
    const operation =
      "listPublications";
    const normalizedDocumentId =
      normalizeDocumentId(documentId);

    try {
      const records =
        await repository.listPublications(
          normalizedDocumentId,
          context
        );

      if (!Array.isArray(records)) {
        throw new PublicationError(
          "Publication repository listPublications() must return an array.",
          {
            code:
              "INVALID_REPOSITORY_RESULT",
            operation,
            documentId:
              normalizedDocumentId,
          }
        );
      }

      return records.map((record) => {
        const publication =
          validateAndClonePublication(record);

        if (
          publication.documentId !==
          normalizedDocumentId
        ) {
          throw new PublicationError(
            "Publication repository returned a publication for another document.",
            {
              code:
                "INVALID_REPOSITORY_RESULT",
              operation,
              publicationId:
                publication.publicationId,
              documentId:
                normalizedDocumentId,
              details: {
                returnedDocumentId:
                  publication.documentId,
              },
            }
          );
        }

        return publication;
      });
    } catch (error) {
      throw wrapPublicationError(
        error,
        "Unable to list Studio publications.",
        {
          code:
            "LIST_PUBLICATIONS_FAILED",
          operation,
          documentId:
            normalizedDocumentId,
        }
      );
    }
  }

  async function unpublish(
    documentId,
    {
      unpublishedBy = null,
      context,
    } = {}
  ) {
    const operation = "unpublish";
    const normalizedDocumentId =
      normalizeDocumentId(documentId);

    try {
      const transition = {
        unpublishedAt:
          normalizeTimestamp(clock()),
        unpublishedBy:
          normalizeOptionalString(
            unpublishedBy,
            "unpublishedBy"
          ),
      };

      const record =
        await repository.unpublishPublication(
          normalizedDocumentId,
          transition,
          context
        );

      if (record === null) {
        return null;
      }

      const publication =
        validateAndClonePublication(record);

      if (
        publication.documentId !==
          normalizedDocumentId ||
        publication.status !==
          PUBLICATION_STATUS.UNPUBLISHED
      ) {
        throw new PublicationError(
          "Publication repository returned an invalid unpublished publication.",
          {
            code:
              "INVALID_REPOSITORY_RESULT",
            operation,
            publicationId:
              publication.publicationId,
            documentId:
              normalizedDocumentId,
          }
        );
      }

      return publication;
    } catch (error) {
      throw wrapPublicationError(
        error,
        "Unable to unpublish Studio document.",
        {
          code: "UNPUBLISH_FAILED",
          operation,
          documentId:
            normalizedDocumentId,
        }
      );
    }
  }

  async function exists(
    publicationId,
    { context } = {}
  ) {
    const operation = "exists";
    const normalizedPublicationId =
      normalizePublicationId(publicationId);

    try {
      const result =
        await repository.exists(
          normalizedPublicationId,
          context
        );

      if (typeof result !== "boolean") {
        throw new PublicationError(
          "Publication repository exists() must return a boolean.",
          {
            code:
              "INVALID_REPOSITORY_RESULT",
            operation,
            publicationId:
              normalizedPublicationId,
          }
        );
      }

      return result;
    } catch (error) {
      throw wrapPublicationError(
        error,
        "Unable to check Studio publication existence.",
        {
          code:
            "PUBLICATION_EXISTS_FAILED",
          operation,
          publicationId:
            normalizedPublicationId,
        }
      );
    }
  }

  return Object.freeze({
    publishVersion,
    getPublication,
    getCurrentPublication,
    listPublications,
    unpublish,
    exists,
  });
}

export function validatePublicationRecord(record) {
  const issues = [];

  if (!isPlainObject(record)) {
    return {
      valid: false,
      issues: [
        createIssue(
          "$",
          "INVALID_PUBLICATION_RECORD",
          "Publication record must be a plain object."
        ),
      ],
    };
  }

  if (
    !Number.isInteger(record.schemaVersion) ||
    record.schemaVersion < 1
  ) {
    issues.push(
      createIssue(
        "schemaVersion",
        "INVALID_SCHEMA_VERSION",
        "schemaVersion must be a positive integer."
      )
    );
  }

  validateRequiredStringField(
    record,
    "publicationId",
    issues
  );
  validateRequiredStringField(
    record,
    "documentId",
    issues
  );
  validateRequiredStringField(
    record,
    "versionId",
    issues
  );
  validateRequiredStringField(
    record,
    "publishedAt",
    issues
  );

  if (
    !Object.values(
      PUBLICATION_STATUS
    ).includes(record.status)
  ) {
    issues.push(
      createIssue(
        "status",
        "INVALID_STATUS",
        "status must be published or unpublished."
      )
    );
  }

  validateOptionalStringField(
    record,
    "publishedBy",
    issues
  );
  validateOptionalStringField(
    record,
    "unpublishedAt",
    issues
  );
  validateOptionalStringField(
    record,
    "unpublishedBy",
    issues
  );

  if (!isPlainObject(record.metadata)) {
    issues.push(
      createIssue(
        "metadata",
        "INVALID_METADATA",
        "metadata must be a plain object."
      )
    );
  }

  if (
    record.status ===
      PUBLICATION_STATUS.PUBLISHED &&
    (
      record.unpublishedAt !== null ||
      record.unpublishedBy !== null
    )
  ) {
    issues.push(
      createIssue(
        "status",
        "INVALID_PUBLISHED_TRANSITION",
        "A published record cannot contain unpublication data."
      )
    );
  }

  if (
    record.status ===
      PUBLICATION_STATUS.UNPUBLISHED &&
    (
      typeof record.unpublishedAt !==
        "string" ||
      record.unpublishedAt.trim().length === 0
    )
  ) {
    issues.push(
      createIssue(
        "unpublishedAt",
        "MISSING_UNPUBLISHED_AT",
        "An unpublished record requires unpublishedAt."
      )
    );
  }

  if (
    Object.prototype.hasOwnProperty.call(
      record,
      "serializedDocument"
    ) ||
    Object.prototype.hasOwnProperty.call(
      record,
      "document"
    )
  ) {
    issues.push(
      createIssue(
        "$",
        "EMBEDDED_DOCUMENT_FORBIDDEN",
        "Publication records must not embed Studio documents."
      )
    );
  }

  if (issues.length === 0) {
    try {
      canonicalizeStudioValue(record);
    } catch (error) {
      issues.push(
        createIssue(
          "$",
          "NON_SERIALIZABLE_PUBLICATION_RECORD",
          error.message
        )
      );
    }
  }

  return {
    valid: issues.length === 0,
    issues,
  };
}

export function assertValidPublicationRecord(
  record
) {
  const result =
    validatePublicationRecord(record);

  if (!result.valid) {
    throw new PublicationError(
      "Publication record validation failed.",
      {
        code:
          "PUBLICATION_RECORD_VALIDATION_FAILED",
        details: {
          issues: result.issues,
        },
      }
    );
  }

  return record;
}

export function clonePublicationRecord(record) {
  return validateAndClonePublication(record);
}

function validateResolvedVersion(
  version,
  expectedVersionId
) {
  if (!isPlainObject(version)) {
    throw new PublicationError(
      "getVersion() must return a LayoutVersion record.",
      {
        code: "INVALID_VERSION_RESULT",
        versionId: expectedVersionId,
      }
    );
  }

  const returnedVersionId =
    normalizeRequiredString(
      version.versionId,
      "versionId",
      "INVALID_VERSION_RESULT"
    );

  if (
    returnedVersionId !==
    expectedVersionId
  ) {
    throw new PublicationError(
      "getVersion() returned another LayoutVersion.",
      {
        code: "INVALID_VERSION_RESULT",
        versionId: expectedVersionId,
        details: {
          returnedVersionId,
        },
      }
    );
  }

  normalizeRequiredString(
    version.documentId,
    "documentId",
    "INVALID_VERSION_RESULT"
  );
}

function validateAndClonePublication(record) {
  assertValidPublicationRecord(record);
  return canonicalizeStudioValue(record);
}

function validateRepository(repository) {
  if (!isPlainObject(repository)) {
    throw new PublicationError(
      "PublicationEngine requires a repository object.",
      {
        code: "INVALID_REPOSITORY",
      }
    );
  }

  for (
    const methodName of
      REQUIRED_REPOSITORY_METHODS
  ) {
    if (
      typeof repository[methodName] !==
      "function"
    ) {
      throw new PublicationError(
        `Publication repository requires ${methodName}().`,
        {
          code:
            "INVALID_REPOSITORY_METHOD",
          details: { methodName },
        }
      );
    }
  }
}

function normalizePublicationId(publicationId) {
  return normalizeRequiredString(
    publicationId,
    "publicationId",
    "INVALID_PUBLICATION_ID"
  );
}

function normalizeDocumentId(documentId) {
  return normalizeRequiredString(
    documentId,
    "documentId",
    "INVALID_DOCUMENT_ID"
  );
}

function normalizeRequiredString(
  value,
  fieldName,
  code
) {
  if (
    typeof value !== "string" ||
    value.trim().length === 0
  ) {
    throw new PublicationError(
      `${fieldName} must be a non-empty string.`,
      { code }
    );
  }

  return value.trim();
}

function normalizeOptionalString(
  value,
  fieldName
) {
  if (value === null || value === undefined) {
    return null;
  }

  if (typeof value !== "string") {
    throw new PublicationError(
      `${fieldName} must be a string or null.`,
      {
        code: `INVALID_${fieldName
          .replace(/([A-Z])/g, "_$1")
          .toUpperCase()}`,
      }
    );
  }

  const normalized = value.trim();
  return normalized.length > 0
    ? normalized
    : null;
}

function normalizeTimestamp(value) {
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) {
      throw new PublicationError(
        "Publication clock returned an invalid Date.",
        {
          code: "INVALID_TIMESTAMP",
        }
      );
    }

    return value.toISOString();
  }

  return normalizeRequiredString(
    value,
    "timestamp",
    "INVALID_TIMESTAMP"
  );
}

function normalizeMetadata(metadata) {
  if (!isPlainObject(metadata)) {
    throw new PublicationError(
      "Publication metadata must be a plain object.",
      {
        code: "INVALID_METADATA",
      }
    );
  }

  return canonicalizeStudioValue(metadata);
}

function validateCallback(callback, name, code) {
  if (typeof callback !== "function") {
    throw new PublicationError(
      `PublicationEngine requires a ${name} callback.`,
      { code }
    );
  }
}

function validateRequiredStringField(
  record,
  fieldName,
  issues
) {
  if (
    typeof record[fieldName] !== "string" ||
    record[fieldName].trim().length === 0
  ) {
    issues.push(
      createIssue(
        fieldName,
        `INVALID_${fieldName
          .replace(/([A-Z])/g, "_$1")
          .toUpperCase()}`,
        `${fieldName} must be a non-empty string.`
      )
    );
  }
}

function validateOptionalStringField(
  record,
  fieldName,
  issues
) {
  if (
    record[fieldName] !== null &&
    typeof record[fieldName] !== "string"
  ) {
    issues.push(
      createIssue(
        fieldName,
        `INVALID_${fieldName
          .replace(/([A-Z])/g, "_$1")
          .toUpperCase()}`,
        `${fieldName} must be a string or null.`
      )
    );
  }
}

function createIssue(path, code, message) {
  return { path, code, message };
}

function wrapPublicationError(
  error,
  message,
  {
    code,
    operation,
    publicationId = null,
    versionId = null,
    documentId = null,
  }
) {
  if (error instanceof PublicationError) {
    return error;
  }

  return new PublicationError(message, {
    code,
    operation,
    publicationId,
    versionId,
    documentId,
    cause: error,
  });
}

function isPlainObject(value) {
  if (
    value === null ||
    typeof value !== "object" ||
    Array.isArray(value)
  ) {
    return false;
  }

  const prototype =
    Object.getPrototypeOf(value);

  return (
    prototype === Object.prototype ||
    prototype === null
  );
}

function createDefaultTimestamp() {
  return new Date().toISOString();
}

function createDefaultPublicationId({
  prefix = "publication",
  documentId = "document",
} = {}) {
  const randomPart =
    typeof globalThis.crypto?.randomUUID ===
    "function"
      ? globalThis.crypto.randomUUID()
      : `${Date.now()}-${Math.random()
          .toString(16)
          .slice(2)}`;

  return `${prefix}-${documentId}-${randomPart}`;
}
